import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = slug === 'all' || !slug
    ? articles
    : articles.filter((a) => a.categorySlug === slug);

  const categoryName = activeCategory?.name || '全部作品';

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${categoryName} - 纲手本子·高质量同人创作合集`,
          description: `浏览${categoryName}分类下的纲手本子同人创作作品，包括插画、漫画和同人故事等精选内容。`,
          url: `https://tsunade-doujin.readdy.ai/category/${slug || 'all'}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-600">
                共 {filteredArticles.length} 篇作品
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    (slug === cat.slug || (cat.slug === 'all' && !slug))
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无作品</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-white text-xs rounded-full font-medium">
                        {article.category}
                      </span>
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h2 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-xs text-foreground-500 pt-3 border-t border-background-200/70">
                        <span className="flex items-center gap-1"><i className="ri-user-line"></i>{article.author}</span>
                        <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTopButton />
      </div>
    </>
  );
}